/**
 * Pub/sub for the InstallInstructionsModal — the platform-agnostic
 * "how to add HomePlated to your Home Screen" modal. Same singleton pattern
 * as pushPermissionModalService.
 *
 * The modal auto-detects platform (iOS Safari vs Android Chrome) at render
 * time; the optional `platform` override lets a caller force a variant.
 */
export type InstallPlatform = 'ios' | 'android';

type State = {
  isOpen: boolean;
  platform?: InstallPlatform;
  // Fired once, after the modal closes. Used to chain a follow-up prompt —
  // e.g. the Android registration flow shows install first, then the push
  // value-pitch once this is dismissed. Not persisted across open() calls.
  onDismiss?: () => void;
};

type Listener = (state: State) => void;

let state: State = { isOpen: false };
const listeners: Set<Listener> = new Set();

function notify() {
  listeners.forEach((l) => l(state));
}

export function openInstallInstructionsModal(options?: {
  platform?: InstallPlatform;
  onDismiss?: () => void;
}) {
  state = { isOpen: true, platform: options?.platform, onDismiss: options?.onDismiss };
  notify();
}

export function closeInstallInstructionsModal() {
  // Capture the callback before clearing so a chained follow-up (e.g. the
  // push value-pitch) fires exactly once, after this modal is fully closed.
  const onDismiss = state.onDismiss;
  state = { isOpen: false };
  notify();
  onDismiss?.();
}

export function subscribeToInstallInstructionsModal(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}
